import * as React from 'react'
import styled from 'styled-components'
import { CastingDayId, CastingSlot } from '../../../../services/models/castings.service'
import { StoreProps, castingStoreGetMultiOpeningHours, castingStoreGetWorkables } from '../../../../services/stores/casting.store'
import { intersection } from '../../../../services/utils/lodash.service'
import { moment } from '../../../../services/utils/moment.service'
import { formatTime, parseTimeRange } from '../../../../services/utils/utils.service'
import { getPeriodsForOpenable, periodI18nName, periodPresets } from '../../../../services/models/periods.service'

const Table = styled.table<{ $isInWidget: boolean }>`
  width: 100%;

  label:before { margin: auto !important; }
  tr { border-bottom: 1px solid ${ props => props.theme.colors.clr_0_light}; }
  td {
    padding: 0 !important;
    ${ props => !props.$isInWidget && 'height: 3.125em !important;' }
  }
`

export default function CastingFrequencyPeriodPickerComponent ({ store, data: { market, slots, renderingMode, offeredService, requiredEmployees, requiredSupplier, serviceDeliveryLocation } }: StoreProps) {
  const isChecked = React.useCallback((dayId: CastingDayId, period: typeof periodPresets[0]) => {
    const slot = slots[0]
    const [periodFrom, periodUntil] = parseTimeRange(period.from_hour, period.until_hour)

    return !!slot.allowed_patterns.find(pattern => {
      const [patternFrom, patternUntil] = parseTimeRange(pattern.from_hour, pattern.until_hour)
      return pattern.day_id === dayId && patternFrom <= periodFrom && periodUntil <= patternUntil
    })
  }, [slots])

  const togglePeriod = React.useCallback((dayId: CastingDayId, period: typeof periodPresets[0]) => {
    const slot = slots[0]
    const flattenDailyPeriod = new Array(48 * 60).fill(false) // one cell per minute in the day
    
    for (const pattern of slot.allowed_patterns) {
      if (pattern.day_id !== dayId) continue
      const [fromMinute, untilMinute] = parseTimeRange(pattern.from_hour, pattern.until_hour).map(_ => Math.round(_ * 60))
      for (let i = fromMinute; i <= untilMinute; i++) flattenDailyPeriod[i] = true
    }

    const wasChecked = isChecked(dayId, period)
    const [periodFrom, periodUntil] = parseTimeRange(period.from_hour, period.until_hour).map(_ => Math.round(_ * 60))
    for (let i = periodFrom; i <= periodUntil; i++) flattenDailyPeriod[i] = !wasChecked
    if (flattenDailyPeriod[periodFrom - 1]) flattenDailyPeriod[periodFrom] = true
    if (flattenDailyPeriod[periodUntil + 1]) flattenDailyPeriod[periodUntil] = true

    const newAllowedPatterns: CastingSlot['allowed_patterns'] = []

    for (let i = 0; i <= flattenDailyPeriod.length; i++) {
      const last = newAllowedPatterns.length && newAllowedPatterns[newAllowedPatterns.length - 1]
      if ((!last || last.until_hour) && flattenDailyPeriod[i]) newAllowedPatterns.push({ day_id: dayId, from_hour: formatTime(i / 60), until_hour: null, additional_days: null })
      else if (last && !last.until_hour && !flattenDailyPeriod[i]) last.until_hour = formatTime((i - 1) / 60)
    }

    store.setSlots([{ ...slot, allowed_patterns: newAllowedPatterns.concat(slot.allowed_patterns.filter(_ => _.day_id !== dayId)) }])
  }, [isChecked, slots, store])

  const workingDays = React.useMemo(() =>
    intersection(castingStoreGetWorkables({ market, offeredService, requiredEmployees, requiredSupplier, serviceDeliveryLocation }).map(_ => _.working_days)),
    [market, offeredService, requiredEmployees, requiredSupplier, serviceDeliveryLocation]
  )

  const periodLayout = React.useMemo(() => {
    const workingDayPeriods = getPeriodsForOpenable(castingStoreGetMultiOpeningHours({
      market, offeredService, requiredEmployees, requiredSupplier, serviceDeliveryLocation, slots
    }).multiOpeningHours)

    return periodPresets
      .map(period => ({
        period,
        workingDayPeriods: workingDays
          .map(workingDay => ({ 
            workingDay: workingDay % 7 as CastingDayId, 
            period: workingDayPeriods[workingDay]
              .find(p => p.name === period.name)
          }))
          .filter(({ period }) => {
            if (!period) return false
            const [periodFrom, periodUntil] = parseTimeRange(period.from_hour, period.until_hour)
            return periodUntil - periodFrom >= offeredService.duration_min / 3600
          })
      })).filter(_ => _.workingDayPeriods.some(__ => __.period))
  }, [market, offeredService, requiredEmployees, requiredSupplier, serviceDeliveryLocation, slots, workingDays])

  return <Table $isInWidget={ renderingMode === 'widget' }>
    <thead>
      <tr>
        <th></th>
        { workingDays.map(dayId => <th key={ dayId } style={{ textAlign: 'center' }}>{ moment().day(dayId).format('dd') }</th>) }
      </tr>
    </thead>

    <tbody>
      { periodLayout.map(p => <tr key={ p.period.name }>
        <td className="js-test-period-name">{ periodI18nName(p.period.name) }</td>
        { p.workingDayPeriods.map(({ workingDay: dayId, period }) => <td key={ dayId }>
          <div className="inputRadio inputRadio--full" style={{ textAlign: 'center' }}>
            { period && <>
              <input type="checkbox" id={ `timespanTable-d${dayId}-${period.from_hour}-${period.until_hour}` } checked={ isChecked(dayId, period) } onChange={ () => togglePeriod(dayId, period) } />
              <label htmlFor={ `timespanTable-d${dayId}-${period.from_hour}-${period.until_hour}` }></label>
            </> }
          </div>
        </td>) }
      </tr>) }
    </tbody>
  </Table>
}
